import { Menu } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@reflex/ui/components/ui/button';
import { ScrollArea } from '@reflex/ui/components/ui/scroll-area';
import { FlowApprovalCard } from '@reflex/plugin-flows/web/components';
import type { DecisionGate } from '@/hooks/useDecisionGates';
import { useSubmitDecisionGate } from '@/hooks/useDecisionGates';

/**
 * Inbox panel for a single neutral decision gate. Producer-agnostic: it reads
 * only the shared `DecisionGate` shape and submits through `/decision-gates`,
 * so the inbox renders any gate (today, flow approvals) without importing the
 * flow system's submit path. Rich reviewer context travels in `gate.context`.
 */
export function DecisionGateDetail({
  gate,
  onToggleSidebar,
}: {
  gate: DecisionGate;
  onToggleSidebar?: () => void;
}) {
  const submit = useSubmitDecisionGate();
  const ctx = (gate.context ?? {}) as Record<string, unknown>;

  const handleSubmit = (response: { approved?: boolean; decision?: string; feedback?: string }) => {
    const decision = response.decision ?? (response.approved === false ? 'rejected' : 'approved');
    submit.mutate(
      { gateId: gate.id, decision, feedback: response.feedback },
      {
        onError: (err: unknown) => {
          toast.error('Could not submit decision', {
            description: err instanceof Error ? err.message : 'Please try again.',
          });
        },
      },
    );
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-border/60 px-4 py-2 md:hidden">
        {onToggleSidebar && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={onToggleSidebar}
            aria-label="Toggle sidebar"
          >
            <Menu className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      <ScrollArea className="flex-1 min-h-0">
        <div className="mx-auto w-full max-w-2xl p-4">
          <FlowApprovalCard
            runId={gate.id}
            nodeId={gate.id}
            nodeName={gate.title}
            approvalType={typeof ctx.approvalType === 'string' ? ctx.approvalType : 'approval'}
            prompt={gate.prompt}
            context={(ctx.context as Record<string, unknown> | null | undefined) ?? null}
            allowFeedback={gate.allowFeedback}
            decisionOptions={gate.options}
            onSubmit={handleSubmit}
            isSubmitting={submit.isPending}
          />
        </div>
      </ScrollArea>
    </div>
  );
}
